import axios from './axiosInstance';
import {
  APIApiKey,
  APIApiKeyCreateResponse,
  CreateApiKeyPayload,
  UpdateApiKeyPayload,
  PreviewApiKey,
  GrantableApiKeyPermissions,
} from '../../types';

// API-key CRUD against the v19 monoRouter routes
// (`/{sport}/areas/{areaId}/apikeys…`). The base URL already carries the API
// version, so paths omit it (mirrors redirect.ts). Errors are rethrown so the
// hook / page can surface a toast.

interface AreaScope {
  sport: string;
  areaId: string;
}

interface CreateApiKeyParams extends AreaScope {
  payload: CreateApiKeyPayload;
}

interface UpdateApiKeyParams extends AreaScope {
  apiKeyId: string;
  payload: UpdateApiKeyPayload;
}

interface DeleteApiKeyParams extends AreaScope {
  apiKeyId: string;
}

const getApiKeys = ({ sport, areaId }: AreaScope): Promise<APIApiKey[]> => {
  return axios.get(`/${sport}/areas/${areaId}/apikeys`)
    .then((response) => response.data);
};

const createApiKey = ({ sport, areaId, payload }: CreateApiKeyParams): Promise<APIApiKeyCreateResponse> => {
  return axios.put(`/${sport}/areas/${areaId}/apikeys`, payload)
    .then((response) => response.data);
};

const updateApiKey = ({ sport, areaId, apiKeyId, payload }: UpdateApiKeyParams): Promise<APIApiKey> => {
  return axios.post(`/${sport}/areas/${areaId}/apikeys/${apiKeyId}`, payload)
    .then((response) => response.data);
};

const deleteApiKey = ({ sport, areaId, apiKeyId }: DeleteApiKeyParams): Promise<{ success: true }> => {
  return axios.delete(`/${sport}/areas/${areaId}/apikeys/${apiKeyId}`)
    .then((response) => response.data);
};

// What the CALLER may delegate to a key. Drives the key editor so it can offer
// only what the write path will accept, instead of showing the full vocabulary
// and failing at submit.
const getGrantableApiKeyPermissions = ({ sport, areaId }: AreaScope): Promise<GrantableApiKeyPermissions> => {
  return axios.get(`/${sport}/areas/${areaId}/apikeys/grantable`)
    .then((response) => response.data);
};

// Mints/returns the ephemeral all-scope preview key for the embed pages' live
// previews (apiKeys:read-gated). Rethrows so the caller can fall back silently.
const getPreviewApiKey = ({ sport, areaId }: AreaScope): Promise<PreviewApiKey> => {
  return axios.get(`/${sport}/areas/${areaId}/apikeys/preview`)
    .then((response) => response.data);
};

const bundledFunctions = {
  getApiKeys,
  createApiKey,
  updateApiKey,
  deleteApiKey,
  getPreviewApiKey,
  getGrantableApiKeyPermissions,
};

export default bundledFunctions;
